import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, ArrowRight, ChevronDown, Printer, Trash2, Trophy, Check } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import useBillingPlan from "@/lib/use-billing-plan";
import UpgradeLiveModal from "@/components/UpgradeLiveModal";
import { onWorkspaceChange } from "@/lib/active-workspace";
import { useAuth } from "@/lib/auth-context";
import GuestGate from "@/components/GuestGate";
import DeliverableFeedback from "@/components/DeliverableFeedback";
/* ------------------------- brief rendering ------------------------- */

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-2">{title}</div>
      {children}
    </div>
  );
}

function BulletList({ items, testid }) {
  if (!items || items.length === 0) return <div className="text-xs text-zinc-500">Not provided.</div>;
  return (
    <ul className="space-y-1.5" data-testid={testid}>
      {items.map((s, i) => (
        <li key={i} className="flex gap-2 text-sm text-zinc-200 leading-relaxed">
          <span className="mt-2 h-1 w-1 rounded-full bg-amber-400/70 flex-shrink-0" />
          <span>{typeof s === "string" ? s : JSON.stringify(s)}</span>
        </li>
      ))}
    </ul>
  );
}

function BriefBody({ content }) {
  if (!content || typeof content !== "object") {
    return <div className="text-xs text-zinc-500">No content available for this brief.</div>;
  }
  // Alias tolerant lookup - the LLM occasionally uses variant field names.
  const pick = (...keys) => {
    for (const k of keys) {
      const v = content[k];
      if (v !== undefined && v !== null && v !== "") return v;
    }
    return undefined;
  };
  const hero_pitch = pick("hero_pitch", "elevator_pitch", "pitch");
  const core_stories = pick("core_stories", "stories", "star_stories");
  const top_predicted_questions = pick(
    "top_predicted_questions", "top_questions_to_prepare", "predicted_questions", "top_questions"
  );
  const your_best_stories = pick("your_best_stories", "best_stories", "best_examples");
  const your_biggest_risks = pick("your_biggest_risks", "focus_areas", "risk_areas", "areas_to_probe");
  const must_hit_keywords = pick("must_hit_keywords", "keywords");
  const key_achievements = pick("key_achievements", "achievements", "wins");
  const smart_questions_to_ask = pick("smart_questions_to_ask", "questions_to_ask", "your_questions");
  const last_minute_reminders = pick("last_minute_reminders", "reminders");
  const confidence_tips = pick("confidence_tips", "confidence_boosters", "mental_reminders");

  return (
    <div className="space-y-6">
      {hero_pitch && (
        <Section title="Your Hero Pitch">
          <p className="text-sm text-zinc-100 leading-relaxed italic border-l-2 border-amber-500/40 pl-4">
            {hero_pitch}
          </p>
        </Section>
      )}

      {Array.isArray(core_stories) && core_stories.length > 0 && (
        <Section title="Core Stories (STAR)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {core_stories.map((cs, i) => (
              <div key={i} className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-3">
                <div className="text-sm font-semibold text-zinc-100">{cs.title || `Story ${i + 1}`}</div>
                {cs.situation && <div className="text-xs text-zinc-400 mt-2"><span className="text-zinc-500">S:</span> {cs.situation}</div>}
                {cs.action && <div className="text-xs text-zinc-400 mt-1"><span className="text-zinc-500">A:</span> {cs.action}</div>}
                {cs.result && <div className="text-xs text-zinc-400 mt-1"><span className="text-zinc-500">R:</span> {cs.result}</div>}
              </div>
            ))}
          </div>
        </Section>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {Array.isArray(top_predicted_questions) && top_predicted_questions.length > 0 && (
          <Section title="Top Predicted Questions">
            <BulletList items={top_predicted_questions} testid="brief-top-questions" />
          </Section>
        )}
        {Array.isArray(your_best_stories) && your_best_stories.length > 0 && (
          <Section title="Your Best Stories to Lead With">
            <BulletList items={your_best_stories} />
          </Section>
        )}
        {Array.isArray(your_biggest_risks) && your_biggest_risks.length > 0 && (
          <Section title="Areas They'll Probe">
            <BulletList items={your_biggest_risks} />
          </Section>
        )}
        {Array.isArray(key_achievements) && key_achievements.length > 0 && (
          <Section title="Key Achievements to Highlight">
            <BulletList items={key_achievements} />
          </Section>
        )}
        {Array.isArray(smart_questions_to_ask) && smart_questions_to_ask.length > 0 && (
          <Section title="Smart Questions to Ask">
            <BulletList items={smart_questions_to_ask} />
          </Section>
        )}
        {Array.isArray(last_minute_reminders) && last_minute_reminders.length > 0 && (
          <Section title="Last-minute Reminders">
            <BulletList items={last_minute_reminders} />
          </Section>
        )}
      </div>

      {Array.isArray(must_hit_keywords) && must_hit_keywords.length > 0 && (
        <Section title="Must-hit Keywords">
          <div className="flex flex-wrap gap-1.5">
            {must_hit_keywords.map((k, i) => (
              <span
                key={i}
                className="text-[11px] rounded-md border border-amber-500/25 bg-amber-500/[0.06] text-amber-200 px-2 py-1"
              >
                {k}
              </span>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(confidence_tips) && confidence_tips.length > 0 && (
        <Section title="Confidence Tips">
          <BulletList items={confidence_tips} />
        </Section>
      )}
    </div>
  );
}

function printBrief(brief) {
  const raw = brief.content || {};
  const pick = (...keys) => {
    for (const k of keys) {
      const v = raw[k];
      if (v !== undefined && v !== null && v !== "") return v;
    }
    return undefined;
  };
  const c = {
    role: raw.role,
    company: raw.company,
    hero_pitch: pick("hero_pitch", "elevator_pitch", "pitch"),
    core_stories: pick("core_stories", "stories", "star_stories"),
    top_predicted_questions: pick("top_predicted_questions", "top_questions_to_prepare", "predicted_questions", "top_questions"),
    your_best_stories: pick("your_best_stories", "best_stories", "best_examples"),
    your_biggest_risks: pick("your_biggest_risks", "focus_areas", "risk_areas", "areas_to_probe"),
    key_achievements: pick("key_achievements", "achievements", "wins"),
    smart_questions_to_ask: pick("smart_questions_to_ask", "questions_to_ask", "your_questions"),
    last_minute_reminders: pick("last_minute_reminders", "reminders"),
    must_hit_keywords: pick("must_hit_keywords", "keywords"),
    confidence_tips: pick("confidence_tips", "confidence_boosters", "mental_reminders"),
  };
  // Escape any string that flows into the printable HTML. Brief content is
  // AI-generated on our own backend but still passes through user-supplied
  // resumes + JDs, so we treat everything as untrusted before serialisation.
  const esc = (val) => {
    if (val === undefined || val === null) return "";
    return String(val)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  };
  const line = (label, items) =>
    items && items.length
      ? `<h3 style="margin-top:16px;color:#111">${esc(label)}</h3><ul>${items
          .map((x) => `<li>${esc(typeof x === "string" ? x : JSON.stringify(x))}</li>`)
          .join("")}</ul>`
      : "";
  const stories = (arr) =>
    arr && arr.length
      ? `<h3 style="margin-top:16px;color:#111">Core Stories</h3>${arr
          .map(
            (s) =>
              `<div style="margin:8px 0"><b>${esc(s.title || "")}</b><br/><small>S:</small> ${esc(s.situation || "")}<br/><small>A:</small> ${esc(s.action || "")}<br/><small>R:</small> ${esc(s.result || "")}</div>`
          )
          .join("")}`
      : "";
  const title = `${esc(c.role || "Interview")}${c.company ? ", " + esc(c.company) : ""}`;
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Interview Day Brief</title>
    <style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;max-width:720px;margin:32px auto;padding:0 24px;color:#111;line-height:1.5}h1{margin:0 0 4px 0}h2{margin-top:24px;color:#111}ul{margin:6px 0 0 20px}small{color:#666}</style>
    </head><body>
      <h1>${title}</h1>
      <small>Interview Day Brief · Generated by Intuiterview</small>
      ${c.hero_pitch ? `<h3 style="margin-top:16px;color:#111">Hero Pitch</h3><blockquote>${esc(c.hero_pitch)}</blockquote>` : ""}
      ${stories(c.core_stories)}
      ${line("Top Predicted Questions", c.top_predicted_questions)}
      ${line("Best Stories to Lead With", c.your_best_stories)}
      ${line("Areas They'll Probe", c.your_biggest_risks)}
      ${line("Key Achievements", c.key_achievements)}
      ${line("Smart Questions to Ask", c.smart_questions_to_ask)}
      ${line("Last-minute Reminders", c.last_minute_reminders)}
      ${line("Must-hit Keywords", c.must_hit_keywords)}
      ${line("Confidence Tips", c.confidence_tips)}
    </body></html>`;
  // Use a Blob URL instead of document.write to avoid the XSS pattern and
  // let the browser parse the HTML in a fresh document context. The Blob is
  // revoked once the print dialog has been triggered.
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (!w) {
    URL.revokeObjectURL(url);
    return;
  }
  w.focus();
  setTimeout(() => {
    try { w.print(); } catch { /* user cancelled or blocked */ }
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }, 500);
}

/* ------------------------- page ------------------------- */

export default function InterviewBriefs() {
  const { user } = useAuth();
  if (user?.is_guest) return <GuestGate feature="get an Interview Day Brief for your own target role" />;
  return <InterviewBriefsInner />;
}

function InterviewBriefsInner() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isFree, refresh: refreshPlan } = useBillingPlan();

  const handleLiveClick = (e) => {
    if (isFree) {
      e.preventDefault();
      setShowUpgrade(true);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/interview/cheat-sheet/list");
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const off = onWorkspaceChange(() => { load(); });
    return () => { off(); };
  }, []);

  // Auto-open a brief if the URL specifies ?id=... (deep link from Dashboard)
  useEffect(() => {
    const target = searchParams.get("id");
    if (target && items.some((x) => x.cheat_sheet_id === target)) {
      setOpenId(target);
    }
  }, [searchParams, items]);

  const removeBrief = async (id) => {
    if (!window.confirm("Delete this Interview Day Brief?")) return;
    setDeletingId(id);
    try {
      await api.delete(`/interview/cheat-sheet/${id}`);
      setItems((xs) => xs.filter((x) => x.cheat_sheet_id !== id));
      if (openId === id) setOpenId(null);
    } catch {
      // ignore
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div data-testid="briefs-page" className="animate-in max-w-3xl xl:max-w-6xl">
      <UpgradeLiveModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        onActivated={async () => {
          await refreshPlan();
          setShowUpgrade(false);
          navigate("/app/live");
        }}
      />
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-2">05 · Interview Brief</div>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-50">Interview Day Briefs</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Printable brief for each interview you&apos;ve prepared for, questions, stories, and reminders in one place.
        </p>
      </div>

      {loading && (
        <div className="text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="p-8 rounded-xl border border-zinc-800/70 bg-zinc-900/40">
          <div className="max-w-md mx-auto text-center">
            <Sparkles className="h-8 w-8 text-zinc-600 mx-auto mb-3" />
            <div className="text-sm text-zinc-300">No Interview Day Briefs yet.</div>
            <div className="text-xs text-zinc-500 mt-1">
              Complete a Practice Interview to warm up, then sit the Official AI Interview
              to earn your Official Readiness Score.
            </div>

            <div className="mt-6 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-amber-400 border border-amber-500/30 bg-amber-500/[0.06] rounded-full px-2.5 py-1">
              <Trophy className="h-3 w-3" />
              Official Readiness Score
            </div>
            <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">
              You&apos;ll receive:
            </div>
            <ul className="grid grid-cols-1 gap-y-1 text-xs text-zinc-300 text-left inline-block mx-auto">
              {[
                "Overall Readiness Score",
                "Hiring Confidence Level",
                "Strength Analysis",
                "Improvement Areas",
                "Interview Report",
              ].map((f) => (
                <li key={f} className="flex items-start gap-1.5">
                  <Check className="h-3 w-3 text-amber-400 mt-0.5 flex-shrink-0" strokeWidth={3} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-2.5">
            <Link
              to="/app/live"
              onClick={handleLiveClick}
              data-testid="briefs-empty-cta-live"
              className="inline-flex items-center gap-2 text-sm bg-amber-400 text-zinc-950 hover:bg-amber-300 font-semibold rounded-lg px-4 py-2 shadow-[0_0_24px_-8px_rgba(251,191,36,0.6)] transition-colors"
            >
              Get my official score
              <span
                data-testid="briefs-empty-cta-live-pro"
                className="text-[9px] uppercase tracking-widest text-zinc-950 border border-zinc-950/40 bg-white/25 rounded-full px-1.5 py-0.5"
              >
                Pro
              </span>
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link
              to="/app/practice?step=video"
              data-testid="briefs-empty-cta-practice"
              className="inline-flex items-center gap-2 text-sm border border-zinc-700/80 text-zinc-200 hover:bg-zinc-900 font-medium rounded-lg px-4 py-2 transition-colors"
            >
              Start Practice Interview <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((c) => {
            const isOpen = openId === c.cheat_sheet_id;
            const role = c.content?.role || "Interview";
            const company = c.content?.company;
            return (
              <div
                key={c.cheat_sheet_id}
                className={`rounded-lg border transition-colors overflow-hidden ${
                  isOpen ? "border-amber-500/40 bg-zinc-900/60" : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700"
                }`}
                data-testid={`brief-item-${c.cheat_sheet_id}`}
              >
                <div className="flex items-center gap-1 p-2 pr-3">
                  <button
                    type="button"
                    onClick={() => setOpenId(isOpen ? null : c.cheat_sheet_id)}
                    aria-expanded={isOpen}
                    data-testid={`brief-item-toggle-${c.cheat_sheet_id}`}
                    className="flex-1 text-left flex items-center justify-between gap-4 p-2 rounded-md hover:bg-zinc-900/40 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Sparkles className="h-4 w-4 text-amber-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm text-zinc-100 truncate">
                          {role}{company ? `, ${company}` : ""}
                        </div>
                        <div className="text-xs text-zinc-500">
                          {new Date(c.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <ChevronDown
                      className={`h-4 w-4 text-zinc-500 flex-shrink-0 transition-transform duration-200 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeBrief(c.cheat_sheet_id)}
                    disabled={deletingId === c.cheat_sheet_id}
                    data-testid={`brief-item-delete-${c.cheat_sheet_id}`}
                    title="Delete this brief"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-zinc-500 hover:text-rose-300 hover:bg-rose-500/[0.08] transition-colors flex-shrink-0 disabled:opacity-60"
                  >
                    {deletingId === c.cheat_sheet_id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>

                {isOpen && (
                  <div
                    className="border-t border-zinc-800/80 p-5 space-y-6"
                    data-testid={`brief-item-body-${c.cheat_sheet_id}`}
                  >
                    <BriefBody content={c.content} />
                    <DeliverableFeedback
                      deliverable="interview_brief"
                      label="Interview Brief"
                      entityKey={c.cheat_sheet_id}
                      context={{ cheat_sheet_id: c.cheat_sheet_id }}
                    />
                    <div className="flex flex-wrap gap-2 pt-4 border-t border-zinc-800/70">
                      <button
                        type="button"
                        onClick={() => printBrief(c)}
                        data-testid={`brief-print-${c.cheat_sheet_id}`}
                        className="inline-flex items-center gap-2 text-xs bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-3 py-1.5"
                      >
                        <Printer className="h-3.5 w-3.5" /> Print brief
                      </button>
                      <button
                        type="button"
                        onClick={() => removeBrief(c.cheat_sheet_id)}
                        disabled={deletingId === c.cheat_sheet_id}
                        data-testid={`brief-delete-${c.cheat_sheet_id}`}
                        className="inline-flex items-center gap-2 text-xs border border-zinc-800 text-zinc-400 hover:text-rose-300 hover:border-rose-500/30 hover:bg-rose-500/[0.06] rounded-lg px-3 py-1.5 disabled:opacity-60"
                      >
                        {deletingId === c.cheat_sheet_id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Trash2 className="h-3.5 w-3.5" />}
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
